import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Popover, type PopoverProps, Space } from 'antd';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { IconExclamationTriangle, IconQuestionCircle, IconQuestionOutline } from '../Icons';

interface Props extends PropsWithChildren {
  title?: string;
  placement?: PopoverProps['placement'];
  variant?: 'description' | 'description-light' | 'warning';
  text?: string;
  suffix?: string;
  underlineText?: boolean;
  boldText?: boolean;
  triggerByHover?: 'icon' | 'both';
  width?: number;
}

export const PopoverHint: FC<Props> = ({
  title,
  children,
  placement,
  variant = 'description-light',
  text = '',
  suffix = '',
  underlineText = false,
  boldText = false,
  triggerByHover = 'icon',
  width,
}) => {
  const icon =
    variant === 'description' ? (
      <IconQuestionCircle />
    ) : variant === 'description-light' ? (
      <IconQuestionOutline />
    ) : (
      <IconExclamationTriangle color={RacemapColors.Orange} />
    );
  let textPrepared: ReactNode = text;
  if (underlineText) textPrepared = <u>{textPrepared}</u>;
  if (boldText) textPrepared = <strong>{textPrepared}</strong>;

  if (triggerByHover === 'icon') {
    return (
      <Space size={3}>
        {textPrepared}
        <Popover
          trigger={['hover', 'click', 'focus']}
          title={title ? <TitleWrapper>{title}</TitleWrapper> : undefined}
          placement={placement}
          content={<ContentWrapper width={width}>{children}</ContentWrapper>}
          styles={{ body: { padding: 0 } }}
          overlayStyle={{ zIndex: 9999 }}
        >
          <IconWrapper onClick={(e) => e.stopPropagation()}>{icon}</IconWrapper>
        </Popover>
        {suffix}
      </Space>
    );
  }
  return (
    <Popover
      trigger={['hover', 'click', 'focus']}
      title={title ? <TitleWrapper>{title}</TitleWrapper> : undefined}
      placement={placement}
      content={<ContentWrapper width={width}>{children}</ContentWrapper>}
      overlayInnerStyle={{ padding: 0 }}
      overlayStyle={{ zIndex: 9999 }}
    >
      {' '}
      <Space size={3}>
        {textPrepared}
        <IconWrapper>{icon}</IconWrapper>
        {suffix}
      </Space>
    </Popover>
  );
};

const IconWrapper = styled.div`
  opacity: 0.7;
  cursor: pointer;
`;

const ContentWrapper = styled.div<{ width?: number }>`
  max-width: 300px;
  padding: 12px;
  ${({ width }) => (width ? `min-width: ${width}px;` : '')}
`;

const TitleWrapper = styled.div`
  font-weight: 550;
  color: ${RacemapColors.DarkDarkGray};
  padding: 12px;
  border-bottom: 1px solid ${RacemapColors.Gray};
  background-color: ${RacemapColors.LightLightGray};
`;
